import { useEffect, useState, useCallback } from "react";
import apiEmailTemplate from "../api";
import { getTemplateValidation } from "../features/template.validation";
import { extractPlaceholders, CAMPAIGN_MERGE_KEYS } from "../features/template.placeholders";

const buildEmptyForm = (mailingFor = "NOTIFICATIONS") => ({
  template_key: "",
  template_name: "",
  mailing_for: mailingFor,
  email_type: "OTHER",
  subject: "",
  eyebrow: "",
  heading: "",
  description: "",
  body_html: "",
  is_activated: true,
});

const STEPS = [
  { key: "basic", label: "Basic Info" },
  { key: "method", label: "Content Method" },
  { key: "content", label: "Content Editor" },
  { key: "test", label: "Test & Finish" },
];

// Central state + orchestration for the multi-step email template wizard.
// Kept as a plain hook (no alerting) so the owning modal decides how to
// surface success/error feedback via showAlert().
export default function useTemplateWizard({ isOpen, id, mailingFor = "NOTIFICATIONS", duplicateFrom = null }) {
  const isEditing = Boolean(id);
  const validators = getTemplateValidation(isEditing);

  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(0);
  const [formData, setFormData] = useState(() => buildEmptyForm(mailingFor));
  const [errors, setErrors] = useState({});

  const [contentMethod, setContentMethod] = useState("manual");
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiGenerated, setAiGenerated] = useState(false);

  const [testEmail, setTestEmail] = useState("");
  const [dataPoints, setDataPoints] = useState({});
  const [testing, setTesting] = useState(false);
  const [isVerifiedThisSession, setIsVerifiedThisSession] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Reset everything each time the wizard opens fresh (create) or for a new id (edit).
  useEffect(() => {
    if (!isOpen) return;

    setStep(0);
    setErrors({});
    setContentMethod("manual");
    setAiPrompt("");
    setAiGenerating(false);
    setAiGenerated(false);
    setTestEmail("");
    setIsVerifiedThisSession(false);
    setSubmitting(false);

    if (!id) {
      const base = buildEmptyForm(mailingFor);
      const initial = duplicateFrom
        ? { ...base, ...duplicateFrom, mailing_for: mailingFor }
        : base;
      setFormData(initial);
      setDataPoints(Object.fromEntries(extractPlaceholders(initial).map((k) => [k, ""])));
      return;
    }

    setLoading(true);
    apiEmailTemplate
      .getById(id)
      .then((res) => {
        const record = res.data?.data || {};
        // Keep the template pinned to the tab it is being managed under.
        setFormData({ ...buildEmptyForm(mailingFor), ...record, mailing_for: mailingFor });
        setDataPoints(
          Object.fromEntries(extractPlaceholders(record).map((k) => [k, ""]))
        );
      })
      .finally(() => setLoading(false));
  }, [isOpen, id, mailingFor]);

  const setField = useCallback((name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => (prev[name] ? { ...prev, [name]: "" } : prev));

    // Editing content after a successful test invalidates that test —
    // this is what makes "must pass a test before saving" meaningful.
    if (["subject", "eyebrow", "heading", "body_html"].includes(name)) {
      setIsVerifiedThisSession(false);
    }
  }, []);

  const placeholders = extractPlaceholders(formData);

  // Fields offered in the editor's "Insert field…" dropdown. Marketing
  // templates get the standard campaign merge tags up front (so authors can
  // insert them without typing), unioned with any custom tokens already used.
  const insertFields =
    formData.mailing_for === "MARKETING"
      ? [...new Set([...CAMPAIGN_MERGE_KEYS, ...placeholders])]
      : placeholders;

  useEffect(() => {
    setDataPoints((prev) => {
      const next = {};
      placeholders.forEach((k) => { next[k] = prev[k] ?? ""; });
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.subject, formData.eyebrow, formData.heading, formData.body_html]);

  const validateFields = (names) => {
    const nextErrors = {};
    names.forEach((name) => {
      const validator = validators[name];
      if (validator) {
        const msg = validator(formData[name], formData);
        if (msg) nextErrors[name] = msg;
      }
    });
    setErrors((prev) => ({ ...prev, ...nextErrors }));
    return Object.keys(nextErrors).length === 0;
  };

  const canGoNextFromStep = (stepIndex) => {
    if (stepIndex === 0) {
      const isMarketing = formData.mailing_for === "MARKETING";
      return Boolean(formData.template_name?.trim()) && (isEditing || isMarketing || Boolean(formData.template_key?.trim()));
    }
    if (stepIndex === 1) {
      return contentMethod === "manual" || aiGenerated;
    }
    if (stepIndex === 2) {
      return Boolean(formData.subject?.trim()) && Boolean(formData.body_html?.trim());
    }
    return true;
  };

  const goNext = () => {
    if (step === 0 && !validateFields(["template_key", "template_name"])) return;
    if (step === 2 && !validateFields(["subject", "body_html"])) return;
    if (!canGoNextFromStep(step)) return;
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };

  const goBack = () => setStep((s) => Math.max(s - 1, 0));

  const generateWithAI = async () => {
    setAiGenerating(true);
    try {
      const res = await apiEmailTemplate.aiGenerate({
        prompt: aiPrompt,
        template_name: formData.template_name,
        mailing_for: formData.mailing_for,
        email_type: formData.email_type,
      });
      const generated = res.data?.data || {};
      setFormData((prev) => ({
        ...prev,
        subject: generated.subject || prev.subject,
        eyebrow: generated.eyebrow ?? prev.eyebrow,
        heading: generated.heading ?? prev.heading,
        body_html: generated.body_html || prev.body_html,
        description: generated.description ?? prev.description,
      }));
      setAiGenerated(true);
      setIsVerifiedThisSession(false);
    } finally {
      setAiGenerating(false);
    }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      await apiEmailTemplate.testDraft({
        subject: formData.subject,
        eyebrow: formData.eyebrow,
        heading: formData.heading,
        body_html: formData.body_html,
        mailing_for: formData.mailing_for,
        email_type: formData.email_type,
        data_points: dataPoints,
        test_email: testEmail,
      });
      setIsVerifiedThisSession(true);
    } finally {
      setTesting(false);
    }
  };

  const finish = async () => {
    setSubmitting(true);
    try {
      const payload = { ...formData, is_verified: isVerifiedThisSession };
      if (isEditing) {
        await apiEmailTemplate.update(id, payload);
      } else {
        await apiEmailTemplate.create(payload);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return {
    STEPS,
    isEditing,
    loading,
    step,
    setStep,
    formData,
    setField,
    errors,
    canGoNext: canGoNextFromStep(step),
    goNext,
    goBack,

    contentMethod,
    setContentMethod,
    aiPrompt,
    setAiPrompt,
    aiGenerating,
    aiGenerated,
    generateWithAI,

    placeholders,
    insertFields,
    testEmail,
    setTestEmail,
    dataPoints,
    setDataPoints,
    testing,
    sendTest,
    isVerifiedThisSession,

    submitting,
    finish,
  };
}
